import React, { SyntheticEvent, useCallback, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete, {
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  createFilterOptions
} from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import RemoveIcon from '@mui/icons-material/RemoveCircle';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@mui/styles';
import { SelectLabelOption } from 'graphql/types';
import { useCustomLabelOptions } from 'modules/customLabels/hooks';
import { CustomLabelTag } from './CustomLabelFacetDetails';

const useAutocompleteStyles = makeStyles((theme) => ({
  tag: {
    background: theme.palette.grey[400],
    height: theme.spacing(3),
    margin: theme.spacing(0.25),
    '& > svg': {
      width: theme.spacing(2)
    }
  },
  loading: {
    fontSize: theme.typography.body2.fontSize
  },
  noOptions: {
    fontSize: theme.typography.body2.fontSize
  },
  option: {
    fontSize: theme.typography.body2.fontSize
  },
  listbox: {
    '& li:last-child': {
      opacity: 1
    }
  }
}));

const useStyles = makeStyles((theme) => ({
  label: {
    paddingRight: theme.spacing(2),
    ...theme.typography.caption,
    fontWeight: 400,
    color: theme.palette.grey[900],
    width: theme.spacing(20),
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  root: {
    display: 'flex',
    alignItems: 'self-end',
    paddingBottom: theme.spacing(2)
  },
  remove: {
    color: theme.palette.error.main,
    padding: theme.spacing(0),
    margin: theme.spacing(0, 0, 0, 1)
  },
  addLi: {
    display: 'flex',
    padding: '0px !important',
    color: theme.palette.primary.main
  },
  li: {
    display: 'flex'
  }
}));

const loadingText = (
  <Typography>
    <FormattedMessage id="customLabels.loading" />
  </Typography>
);

type CustomLabelValueSelectorProps = {
  customLabel: CustomLabelTag;
  values?: CustomLabelOption[] | null;
  onChange: (value: SelectLabelOption[] | null) => void;
  onRemove: () => void;
};
type CustomLabelOption = SelectLabelOption & { inputValue?: string };

export const CustomLabelValueSelector: React.FC<CustomLabelValueSelectorProps> = ({
  customLabel,
  values: defaultValues,
  onChange,
  onRemove
}: CustomLabelValueSelectorProps) => {
  const classes = useStyles();
  const autoCompleteClasses = useAutocompleteStyles();
  const [queryText, setQueryText] = useState('');

  const { loading: optionsLoading, options } = useCustomLabelOptions({
    labelId: customLabel?.id ?? undefined,
    queryText
  });
  const getOptionLabel = useCallback(
    (option: CustomLabelOption) => option.name ?? '',
    []
  );

  const getOptionDisabled = useCallback(
    (option: CustomLabelOption) => {
      return (defaultValues ?? []).some((v) => v.id === option.id);
    },
    [defaultValues]
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option) => {
      return (
        <li
          {...props}
          className={classNames(classes.li, props.className)}
          data-testid={`li-${option.id}`}
        >
          <Tooltip title={option.name} disableInteractive>
            <Typography variant="inherit" noWrap>
              {option.name}
            </Typography>
          </Tooltip>
        </li>
      );
    },
    [classes.li]
  );

  const filterOptions = useCallback((options: CustomLabelOption[], state) => {
    const defaultFilterOptions = createFilterOptions<CustomLabelOption>();
    const filteredOptions = defaultFilterOptions(options, state);

    return filteredOptions;
  }, []);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        variant="standard"
        size="small"
        inputProps={{
          ...params.inputProps,
          'data-testid': `custom-label-value-input-${customLabel.id}`
        }}
      />
    ),
    [customLabel.id]
  );

  const onSearch = useCallback(
    (
      _: SyntheticEvent,
      value: string,
      reason: AutocompleteInputChangeReason
    ) => {
      if (reason !== 'reset') {
        setQueryText(value);
      }
    },
    []
  );

  const onClose = useCallback(() => {
    setQueryText('');
  }, []);

  const handleChange = useCallback(
    (_, value: CustomLabelOption | CustomLabelOption[] | null) =>
      onChange(!value || Array.isArray(value) ? value : [value]),
    [onChange]
  );

  return (
    <div
      className={classes.root}
      data-testid={`cl-key-value-root-${customLabel.name?.toLowerCase()}`}
    >
      <Tooltip title={customLabel.name ?? ''} disableInteractive>
        <Typography
          variant="caption"
          className={classes.label}
          data-testid={`cl-key-${customLabel.id}`}
        >
          {customLabel.name}
        </Typography>
      </Tooltip>
      <Autocomplete<CustomLabelOption, boolean>
        classes={autoCompleteClasses}
        defaultValue={defaultValues}
        multiple
        data-testid={`custom-label-value-search-${customLabel.id}`}
        options={options}
        fullWidth
        loadingText={loadingText}
        loading={optionsLoading}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        getOptionDisabled={getOptionDisabled}
        renderOption={renderOption}
        filterOptions={filterOptions}
        renderInput={renderInput}
        onClose={onClose}
        onInputChange={onSearch}
      />
      <IconButton
        onClick={onRemove}
        className={classes.remove}
        data-testid={`remove-cl-${customLabel.id}`}
      >
        <RemoveIcon />
      </IconButton>
    </div>
  );
};
